import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export interface UseAppPageLayoutOptions {
  /**
   * Array of path patterns that should be treated as chat interfaces.
   * These paths will have no bottom margin and may affect padding.
   */
  chatInterfacePaths?: string[];
  /**
   * Regex patterns (as strings) for paths that should be treated as chat
   * interfaces. Used for parameterised routes such as `/projects/:projectId`
   * where exact-string matching does not apply.
   */
  chatInterfacePathPatterns?: string[];
  /**
   * Array of path patterns that should disable side padding.
   * If not provided, defaults to chat interface paths plus 'cohortbuilder'.
   */
  disableSidePaddingPaths?: string[];
}

export interface UseAppPageLayoutResult {
  /**
   * Whether the current path is a chat interface.
   * Chat interfaces typically have no bottom margin.
   */
  isChatInterface: boolean;
  /**
   * Whether side padding should be disabled for the current path.
   */
  disableSidePadding: boolean;
}

/**
 * Behavior hook that determines layout behavior based on the current route.
 * Encapsulates logic for determining chat interface and padding behavior.
 */
export const useAppPageLayout = (options: UseAppPageLayoutOptions = {}): UseAppPageLayoutResult => {
  const location = useLocation();
  const {
    chatInterfacePaths = [],
    chatInterfacePathPatterns = [],
    disableSidePaddingPaths = [],
  } = options;

  const { isChatInterface, disableSidePadding } = useMemo(() => {
    const pathname = location.pathname;
    const lowerPath = pathname.toLowerCase();

    // Determine if current path is a chat interface (exact-match list or regex patterns).
    const isChatInterfaceValue =
      chatInterfacePaths.some((path) => lowerPath === path.toLowerCase()) ||
      chatInterfacePathPatterns.some((pattern) => new RegExp(pattern).test(pathname));

    // disables side padding if the current path includes any of the disable side padding paths or is a chat interface
    const disableSidePaddingValue =
      disableSidePaddingPaths.some((path) => lowerPath.includes(path.toLowerCase())) ||
      isChatInterfaceValue;

    return {
      isChatInterface: isChatInterfaceValue,
      disableSidePadding: disableSidePaddingValue,
    };
  }, [location.pathname, chatInterfacePaths, chatInterfacePathPatterns, disableSidePaddingPaths]);

  return {
    isChatInterface,
    disableSidePadding,
  };
};
